// src/components/Navbar.jsx
import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const baseLink =
    "text-sm text-ink-muted hover:text-[var(--brand-1)] transition-colors";

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 16);
    };

    onScroll();
    window.addEventListener("scroll", onScroll);

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenus = () => {
    setMobileOpen(false);
    setServicesOpen(false);
  };

  const scrollToTop = () => {
    const doScroll = () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(doScroll, 100);
    } else {
      doScroll();
    }

    closeMenus();
  };

  const scrollToId = (id) => {
    const navOffset = 80;

    const doScroll = () => {
      const el = document.getElementById(id);
      if (!el) return;

      const y = el.getBoundingClientRect().top + window.scrollY - navOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    };

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(doScroll, 100);
    } else {
      doScroll();
    }

    closeMenus();
  };

  return (
    <header className="sticky top-0 z-50">
      <div
        className={`bg-transparent transition-all duration-300 ${
          scrolled ? "py-1" : "py-3"
        }`}
      >
        <div className="mx-auto max-w-7xl px-4">
          <div
            className={`flex items-center justify-between border border-slate-200/70 bg-white/80 shadow-[0_18px_45px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-all duration-300 pointer-events-auto ${
              scrolled ? "h-12 rounded-2xl" : "h-14 rounded-3xl"
            }`}
          >
            {/* LOGO */}
            <button
              type="button"
              onClick={scrollToTop}
              className="ml-4 flex items-center gap-2 font-semibold tracking-tight"
            >
              <img
                src="/img/logotipo-original.svg"
                alt="Zentra Group"
                className="h-8 w-auto"
              />

              <span className="bg-gradient-to-r from-[var(--brand-1)] to-[var(--brand-4)] bg-clip-text text-transparent">
                Zentra Group
              </span>
            </button>

            {/* NAV DESKTOP */}
            <nav className="hidden items-center gap-8 md:flex">
              <button
                type="button"
                onClick={() => scrollToId("nosotros")}
                className={baseLink}
              >
                Nosotros
              </button>

              {/* SERVICIOS DESKTOP */}
              <div
                className="relative -my-3 py-3"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setServicesOpen((prev) => !prev)}
                  className={`${baseLink} inline-flex items-center gap-1`}
                >
                  Servicios
                  <svg
                    viewBox="0 0 20 20"
                    className={`h-4 w-4 transition-transform ${
                      servicesOpen ? "rotate-180" : ""
                    }`}
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {servicesOpen && (
                  <div className="absolute left-0 top-full z-50 mt-1 w-56 rounded-2xl border border-slate-200 bg-white py-2 shadow-[0_18px_40px_rgba(15,23,42,0.12)]">
                    <button
                      type="button"
                      onClick={() => scrollToId("servicios")}
                      className="block w-full px-4 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 hover:text-[var(--brand-1)]"
                    >
                      Detalle general
                    </button>

                    <button
                      type="button"
                      onClick={() => scrollToId("servicios")}
                      className="block w-full px-4 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 hover:text-[var(--brand-1)]"
                    >
                      Zentra Sistemas
                    </button>

                    <button
                      type="button"
                      onClick={() => scrollToId("servicios")}
                      className="block w-full px-4 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 hover:text-[var(--brand-1)]"
                    >
                      Zentra POS
                    </button>

                    <button
                      type="button"
                      onClick={() => scrollToId("pricing")}
                      className="block w-full px-4 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 hover:text-[var(--brand-1)]"
                    >
                      Zentra GPS
                    </button>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => scrollToId("dudas")}
                className={baseLink}
              >
                Dudas Frecuentes
              </button>
            </nav>

            {/* CLIENTE DESKTOP */}
            <div className="mr-4 hidden items-center gap-3 md:flex">
              <NavLink
                to="/cliente"
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm shadow-sm transition btn-brand ${
                    isActive
                      ? "ring-2 ring-[var(--brand-3)]"
                      : "hover:brightness-110"
                  }`
                }
                onClick={closeMenus}
              >
                Cliente
              </NavLink>
            </div>

            {/* BOTÓN MÓVIL */}
            <button
              type="button"
              className="mr-3 inline-flex flex-col items-center justify-center rounded-md bg-transparent p-2 transition-colors hover:bg-slate-100 md:hidden"
              onClick={() => {
                setMobileOpen((prev) => !prev);
                setServicesOpen(false);
              }}
              aria-label="Abrir menú de navegación"
              aria-expanded={mobileOpen}
            >
              <span
                className={`block h-[2px] w-6 rounded-full bg-[var(--brand-1)] transition-transform duration-200 ${
                  mobileOpen ? "translate-y-[5px] rotate-45" : ""
                }`}
              />
              <span
                className={`my-[3px] block h-[2px] w-6 rounded-full bg-[var(--brand-3)] transition-opacity duration-200 ${
                  mobileOpen ? "opacity-0" : "opacity-100"
                }`}
              />
              <span
                className={`block h-[2px] w-6 rounded-full bg-[var(--brand-4)] transition-transform duration-200 ${
                  mobileOpen ? "-translate-y-[5px] -rotate-45" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* MENÚ MÓVIL */}
      {mobileOpen && (
        <div className="border-t border-slate-100 bg-white/95 backdrop-blur md:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-4">
            <button
              type="button"
              onClick={() => scrollToId("nosotros")}
              className={`${baseLink} text-left`}
            >
              Nosotros
            </button>

            <div className="mt-1 border-t border-slate-100 pt-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Servicios
              </p>

              <button
                type="button"
                onClick={() => scrollToId("servicios")}
                className={`${baseLink} block w-full py-1 text-left`}
              >
                Detalle general
              </button>

              <button
                type="button"
                onClick={() => scrollToId("servicios")}
                className={`${baseLink} block w-full py-1 text-left`}
              >
                Zentra Sistemas
              </button>

              <button
                type="button"
                onClick={() => scrollToId("servicios")}
                className={`${baseLink} block w-full py-1 text-left`}
              >
                Zentra POS
              </button>

              <button
                type="button"
                onClick={() => scrollToId("pricing")}
                className={`${baseLink} block w-full py-1 text-left`}
              >
                Zentra GPS
              </button>
            </div>

            <button
              type="button"
              onClick={() => scrollToId("dudas")}
              className={`${baseLink} text-left`}
            >
              Dudas Frecuentes
            </button>

            {/* CLIENTE MÓVIL HABILITADO */}
            <div className="mt-3 border-t border-slate-100 pt-3">
              <NavLink
                to="/cliente"
                className={({ isActive }) =>
                  `block rounded-xl px-4 py-3 text-center text-sm shadow-sm transition btn-brand ${
                    isActive
                      ? "ring-2 ring-[var(--brand-3)]"
                      : "hover:brightness-110"
                  }`
                }
                onClick={closeMenus}
              >
                Cliente
              </NavLink>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}